<template>
    <div class="page">
        <card v-if="uploadResponse !== null" class="mb-3" can-close="true" scroll-on-show="true">
            <template slot="header">
                <strong>Import Complete</strong>
            </template>

            <template slot="body">
                <div class="flex jc-sb ai-fs">
                    <div class="flex-50">
                        <p>
                            <span class="mr-2 primary-text">
                                <i class="material-icons">check</i>
                            </span>
                            {{ uploadResponse.insert_count }} tenants were successfully imported.
                        </p>

                        <p v-if="uploadResponse.active_tenants_count !== 0">
                            <span class="mr-2 warning-text">
                                <i class="material-icons">warning</i>
                            </span>
                            {{ uploadResponse.active_tenants_count }} tenants are already active in the system.
                        </p>

                        <p v-if="uploadResponse.error_count !== 0">
                            <span class="mr-2 danger-text">
                                <i class="material-icons">warning</i>
                            </span>
                            {{ uploadResponse.error_count }} tenants had a Prime Site ID not in the system.
                        </p>

                        <p v-if="uploadResponse.duplicate_tenant_filename">
                            <span class="mr-2 warning-text">
                                <i class="material-icons">warning</i>
                            </span>

                            {{ uploadResponse.duplicate_tenant_count }} tenants have already gone through the system. <a :href="`${api_base_path}/api/exports/download?file_key=${uploadResponse.duplicate_tenant_filename}&filename=duplicate_tenants_${today}`" target="_blank">Download list</a>
                        </p>
                    </div>
                </div>
            </template>
        </card>

        <card class="o-a">
            <template slot="body">
                <div class="flex jc-sb mb-3">
                    <div class="flex-33 mr-2">
                        <button
                            title="Filters"
                            type="button"
                            :disabled="is_loading"
                            :class="{ 'primary': showFilters }"
                            @click="showFilters = !showFilters">
                            <i class="material-icons">filter_list</i>
                            <span v-if="showFilters">Hide Extra Filters</span>
                            <span v-else>Show Extra Filters</span>
                        </button>
                    </div>

                    <div class="flex-33 ml-2 flex jc-fe ai-fe">
                        <button class="mr-3" type="button" @click="onNewCycle">
                            <i class="material-icons">add_circle</i>
                            New Cycle
                        </button>

                        <button class="mr-3" type="reset" @click="onResetFilters">
                            <i class="material-icons">clear_all</i>
                            Reset Filters
                        </button>

                        <button title="Refresh" type="button" :disabled="is_loading" @click="onLoadData">
                            <i class="material-icons">refresh</i>
                        </button>
                    </div>
                </div>

                <div class="flex jc-sb mb-3">
                    <div class="flex">
                        <div>
                            <label for="facility-number">Facility Number</label>
                            <input id="facility-number" type="text" v-model="filters.facility_number" />
                        </div>

                        <div class="ml-5">
                            <label for="account-name">Account Name</label>
                            <input id="account-name" type="text" v-model="filters.account_name" />
                        </div>

                        <div class="ml-5">
                            <label for="corporate-group">Corporate Group</label>
                            <input id="corporate-group" type="text" v-model="filters.corporate_group" />
                        </div>

                        <div class="ml-5" data-cy="insurance-program-filter">
                            <label for="insurance-program">Insurance Program</label>
                            <insurance-program-select @change="(key) => filters.insurance_program_key = key" />
                        </div>
                    </div>

                    <div class="ml-2" style="width: 300px;" data-cy="more-filters">
                        <label>More Filters:</label>
                        <select-input
                            :disabled="is_loading"
                            :active-index="active_filter_index"
                            :choices="filter_options"
                            @change="onChangeFilter" />
                    </div>
                </div>

                <div v-show="showFilters" class="flex jc-sb mb-3">
                    <div class="flex-25 mr-2">
                        <table border="0" cellpadding="0" cellspacing="0">
                            <tr>
                                <td></td>
                                <td>
                                    <label>Initial Letter Date</label>
                                </td>
                            </tr>
                            <tr>
                                <td class="pr-3">
                                    <label class="mb-0">From</label>
                                </td>
                                <td data-cy="initial-letter-date-from-filter">
                                    <datepicker :typeable="true" v-model="dateFilters.initial_letter_date.start" format="MM/dd/yyyy" />
                                </td>
                            </tr>
                            <tr>
                                <td class="pt-3 pr-3">
                                    <label class="mb-0">To</label>
                                </td>
                                <td class="pt-3" data-cy="initial-letter-date-to-filter">
                                    <datepicker :typeable="true" v-model="dateFilters.initial_letter_date.end" format="MM/dd/yyyy" />
                                </td>
                            </tr>
                        </table>
                    </div>

                    <div class="flex-25 ml-2 mr-2">
                        <table border="0" cellpadding="0" cellspacing="0">
                            <tr>
                                <td></td>
                                <td>
                                    <label>Lease Effective Date</label>
                                </td>
                            </tr>
                            <tr>
                                <td class="pr-3">
                                    <label class="mb-0">From</label>
                                </td>
                                <td data-cy="lease-effective-date-from-filter">
                                    <datepicker :typeable="true" v-model="dateFilters.lease_effective_date.start" format="MM/dd/yyyy" />
                                </td>
                            </tr>
                            <tr>
                                <td class="pt-3 pr-3">
                                    <label class="mb-0">To</label>
                                </td>
                                <td class="pt-3" data-cy="lease-effective-date-to-filter">
                                    <datepicker :typeable="true" v-model="dateFilters.lease_effective_date.end" format="MM/dd/yyyy" />
                                </td>
                            </tr>
                        </table>
                    </div>

                    <div class="flex-25 ml-2 mr-2">
                        <table border="0" cellpadding="0" cellspacing="0">
                            <tr>
                                <td></td>
                                <td>
                                    <label>Reminder Letter Date</label>
                                </td>
                            </tr>
                            <tr>
                                <td class="pr-3">
                                    <label class="mb-0">From</label>
                                </td>
                                <td data-cy="reminder-letter-date-from-filter">
                                    <datepicker :typeable="true" v-model="dateFilters.reminder_letter_date.start" format="MM/dd/yyyy" />
                                </td>
                            </tr>
                            <tr>
                                <td class="pt-3 pr-3">
                                    <label class="mb-0">To</label>
                                </td>
                                <td class="pt-3" data-cy="reminder-letter-date-to-filter">
                                    <datepicker :typeable="true" v-model="dateFilters.reminder_letter_date.end" format="MM/dd/yyyy" />
                                </td>
                            </tr>
                        </table>
                    </div>

                    <div class="flex-25 ml-2">
                        <table border="0" cellpadding="0" cellspacing="0">
                            <tr>
                                <td></td>
                                <td>
                                    <label>Letter Effective Date</label>
                                </td>
                            </tr>
                            <tr>
                                <td class="pr-3">
                                    <label class="mb-0">From</label>
                                </td>
                                <td data-cy="letter-effective-date-from-filter">
                                    <datepicker :typeable="true" v-model="dateFilters.letter_effective_date.start" format="MM/dd/yyyy" />
                                </td>
                            </tr>
                            <tr>
                                <td class="pt-3 pr-3">
                                    <label class="mb-0">To</label>
                                </td>
                                <td class="pt-3" data-cy="letter-effective-date-to-filter">
                                    <datepicker :typeable="true" v-model="dateFilters.letter_effective_date.end" format="MM/dd/yyyy" />
                                </td>
                            </tr>
                        </table>
                    </div>
                </div>

                <div class="table-wrapper">
                    <vue-table ref="cycles_table"
                        data-cy="cycles-table"
                        :class="['table-loads', { 'loading': is_loading }]"
                        api-url="/api/cycles"
                        :per-page="100"
                        table-height="475px"
                        :append-params="api_params"
                        data-path="data"
                        pagination-path="pagination"
                        :row-class="onRowClass"
                        :fields="columns"
                        :http-fetch="onHttpFetch"
                        no-data-template="<span>No cycles to show.</span>"
                        detail-row-component="cycles-table-detail-row"
                        @vuetable:loading="is_loading = true"
                        @vuetable:loaded="is_loading = false"
                        @vuetable:load-success="onTableLoadSuccess"
                        @vuetable:row-clicked="onToggleDetailRow">
                        <template slot="facility_number" slot-scope="props">
                            <span class="facility-number">{{ props.rowData.facility_number }}</span>

                            <span :class="['badge', { primary: props.rowData.insurance_program_key === 'aig' }]" data-cy="insurance-program-badge">
                                {{ props.rowData.insurance_program_label }}
                            </span>
                        </template>

                        <template slot="account_name" slot-scope="props">
                            <span class="account-name" :title="(props.rowData.account_name && props.rowData.account_name.length > 35) ? (props.rowData.account_name) : null">{{ props.rowData.account_name }}</span>
                        </template>

                        <template slot="label" slot-scope="props">
                            <span class="label" :title="(props.rowData.label && props.rowData.label.length > 35) ? (props.rowData.label) : null">{{ props.rowData.label }}</span>
                        </template>

                        <template slot="confirmed_move_outs" slot-scope="props">
                            <div v-if="props.rowData.monitor_confirmed_move_outs" title="Monitor Confirmed Move-Outs">
                                <span class="danger-text cu-h">
                                    <i class="material-icons">done</i>
                                </span>
                            </div>

                            <div v-else-if="props.rowData.confirmed_move_outs_date !== null" title="Confirmed Move-Outs">
                                <span class="primary-text cu-h">
                                    <i class="material-icons">done</i>
                                </span>
                            </div>
                            <div v-else-if="props.rowData.confirmed_no_move_outs_date !== null" title="Confirmed No Move-Outs">
                                <span class="secondary-text cu-h">
                                    <i class="material-icons">done</i>
                                </span>
                            </div>
                            <div v-else>
                                <button
                                    :disabled="loading_confirm_facility_id === props.rowData.id"
                                    class="small"
                                    type="button"
                                    @click="onCheckConfirmedStatus(props.rowData)">
                                    Confirm
                                </button>
                            </div>
                        </template>

                        <template slot="initial_letter_send_date" slot-scope="props">
                            <div data-cy="initial-letter-send-date-column">
                                <span v-if="props.rowData.initial_letter_send_date === null" class="null">NULL</span>
                                <span v-else>{{ formatDateRaw(props.rowData.initial_letter_send_date) }}</span>
                            </div>
                        </template>

                        <template slot="lease_effective_date" slot-scope="props">
                            <div data-cy="lease-effective-date-column">
                                <span v-if="props.rowData.lease_effective_date === null" class="null">NULL</span>
                                <span v-else>{{ formatDateRaw(props.rowData.lease_effective_date) }}</span>
                            </div>
                        </template>

                        <template slot="reminder_letter_send_date" slot-scope="props">
                            <div data-cy="reminder-letter-send-date-column">
                                <span v-if="props.rowData.reminder_letter_send_date === null" class="null">NULL</span>
                                <span v-else>{{ formatDateRaw(props.rowData.reminder_letter_send_date) }}</span>
                            </div>
                        </template>

                        <template slot="letter_effective_date" slot-scope="props">
                            <div data-cy="letter-effective-date-column">
                                <span v-if="props.rowData.letter_effective_date === null" class="null">NULL</span>
                                <span v-else>{{ formatDateRaw(props.rowData.letter_effective_date) }}</span>
                            </div>
                        </template>

                        <template slot="update_responses_deadline_date" slot-scope="props">
                            <div data-cy="update-responses-deadline-date-column">
                                <span v-if="props.rowData.update_responses_deadline_date === null" class="null">NULL</span>
                                <span v-else>{{ formatDateRaw(props.rowData.update_responses_deadline_date) }}</span>
                            </div>
                        </template>

                        <template slot="tag_for_removal_deadline_date" slot-scope="props">
                            <div data-cy="tag-for-removal-deadline-date-column">
                                <span v-if="props.rowData.tag_for_removal_deadline_date === null" class="null">NULL</span>
                                <span v-else>{{ formatDateRaw(props.rowData.tag_for_removal_deadline_date) }}</span>
                            </div>
                        </template>

                        <template slot="setup_coverage_dates" slot-scope="props">
                            <div class="flex ai-c jc-sb" data-cy="setup-coverage-dates-column">
                                <div class="flex-1">
                                    <span v-if="props.rowData.setup_coverage_start_date === null" class="gray-message">--</span>
                                    <span v-else>{{ formatDateRaw(props.rowData.setup_coverage_start_date) }}</span>
                                </div>

                                <span class="flex-0 gray-message fs-09 ml-2 mr-2">to</span>

                                <div class="flex-1">
                                    <span v-if="props.rowData.setup_coverage_end_date === null" class="gray-message">--</span>
                                    <span v-else>{{ formatDateRaw(props.rowData.setup_coverage_end_date) }}</span>
                                </div>
                            </div>
                        </template>

                        <template slot="monitor_payment_dates" slot-scope="props">
                            <div class="flex ai-c jc-sb" data-cy="monitor-payment-dates-column">
                                <div class="flex-1">
                                    <span v-if="props.rowData.monitor_payment_start_date === null" class="gray-message">--</span>
                                    <span v-else>{{ formatDateRaw(props.rowData.monitor_payment_start_date) }}</span>
                                </div>

                                <span class="flex-0 gray-message fs-09 ml-2 mr-2">to</span>

                                <div class="flex-1">
                                    <span v-if="props.rowData.monitor_payment_end_date === null" class="gray-message">--</span>
                                    <span v-else>{{ formatDateRaw(props.rowData.monitor_payment_end_date) }}</span>
                                </div>
                            </div>
                        </template>
                    </vue-table>
                </div>
            </template>
        </card>
    </div>
</template>

<script>
import Vue from "vue"
import dayjs from "dayjs"
import http from "@/http"
import { debounce } from "lodash"
import { parseQueryParameters } from "@/utilities/path"
import { convertDate } from "@/utilities/form-data"
import ImportTenantsModal from "@/components/manage-cycles-page/ImportTenantsModal.vue"
import EditCycleModal from "@/components/manage-cycles-page/EditCycleModal.vue"
import NewCycleModal from "@/components/manage-cycles-page/NewCycleModal.vue"
import { formatDateRaw, formatInteger } from "@/utilities/columns"

export default {
    name: "manage-cycles-page",

    data () {
        return {
            active_filter_index: 0,
            api_base_path: http.defaults.baseURL,
            columns: [
                {
                    name: "__slot:facility_number",
                    title: "Facility Number",
                    width: "175px"
                },
                {
                    name: "__slot:account_name",
                    title: "Account Name",
                    width: "300px"
                },
                {
                    name: "corporate_group",
                    title: "Corporate Group",
                    width: "175px"
                },
                {
                    name: "__slot:label",
                    title: "Label",
                    width: "300px"
                },
                {
                    name: "tenant_count",
                    title: "Tenant Count",
                    dataClass: "center aligned",
                    titleClass: "center aligned",
                    callback: formatInteger,
                    width: "125px"
                },
                {
                    name: "__slot:confirmed_move_outs",
                    title: "Confirmed Move-Outs",
                    width: "200px",
                    dataClass: "center aligned no-click",
                    titleClass: "center aligned"
                },
                {
                    name: "__slot:initial_letter_send_date",
                    title: "Initial Letter Date",
                    dataClass: "center aligned",
                    titleClass: "center aligned",
                    width: "200px"
                },
                {
                    name: "__slot:lease_effective_date",
                    title: "Lease Effective Date",
                    dataClass: "center aligned",
                    titleClass: "center aligned",
                    width: "200px"
                },
                {
                    name: "__slot:reminder_letter_send_date",
                    title: "Reminder Letter Date",
                    dataClass: "center aligned",
                    titleClass: "center aligned",
                    width: "200px"
                },
                {
                    name: "__slot:letter_effective_date",
                    title: "Letter Effective Date",
                    dataClass: "center aligned",
                    titleClass: "center aligned",
                    width: "200px"
                },
                {
                    name: "__slot:update_responses_deadline_date",
                    title: "Update Responses Deadline Date",
                    dataClass: "center aligned",
                    titleClass: "center aligned",
                    width: "270px"
                },
                {
                    name: "__slot:tag_for_removal_deadline_date",
                    title: "Tag for Removal Deadline Date",
                    dataClass: "center aligned",
                    titleClass: "center aligned",
                    width: "270px"
                },
                {
                    name: "__slot:setup_coverage_dates",
                    title: "Setup Coverage Dates",
                    dataClass: "center aligned",
                    titleClass: "center aligned",
                    width: "240px"
                },
                {
                    name: "__slot:monitor_payment_dates",
                    title: "Monitor Payment Dates",
                    dataClass: "center aligned",
                    titleClass: "center aligned",
                    width: "240px"
                }
            ],
            filter_options: [
                { key: "total", label: "Show All", badge: null },
                { key: "doing_move_outs", label: "Move-Outs In Progress", badge: null },
                { key: "confirmed_move_outs", label: "Confirmed Move-Outs", badge: null },
                { key: "confirmed_no_move_outs", label: "Confirmed No Move-Outs", badge: null },
                { key: "divider-01", divider: true },
                { key: "opt_outs_threshold", label: "Opt-Outs Threshold", badge: null },
                { key: "provided_proof_threshold", label: "Provided Proof Threshold", badge: null },
                { key: "bad_address_threshold", label: "Bad Address Threshold", badge: null },
                { key: "moved_out_threshold", label: "Moved Out Threshold", badge: null },
                { key: "divider-02", divider: true },
                { key: "initial_letters_unsent", label: "Audit Letters Not Sent", badge: null },
                { key: "reminder_letters_unsent", label: "Reminder Letters Not Sent", badge: null },
                { key: "welcome_letters_unsent", label: "Welcome Letters Not Sent", badge: null }
            ],
            filters: {
                account_name: "",
                corporate_group: "",
                facility_number: "",
                insurance_program_key: ""
            },
            formatDateRaw,
            dateFilters: {
                initial_letter_date: { start: "", end: "" },
                lease_effective_date: { start: "", end: "" },
                reminder_letter_date: { start: "", end: "" },
                letter_effective_date: { start: "", end: "" }
            },
            is_loading: false,
            loading_confirm_facility_id: null,
            newId: null,
            showFilters: false,
            today: dayjs().format("YYYY-MM-DD"),
            uploadResponse: null
        }
    },

    computed: {
        api_params () {
            const params = {}

            if (this.active_filter_index > 0) {
                params.filter = this.filter_options[this.active_filter_index].key
            }

            for (const key in this.filters) {
                params[key] = this.filters[key]
            }

            for (const key in this.dateFilters) {
                const filter = this.dateFilters[key]

                if (filter.start) {
                    params[`${key}_start`] = convertDate(filter.start).split(" ")[0]
                }

                if (filter.end) {
                    params[`${key}_end`] = convertDate(filter.end).split(" ")[0]
                }
            }

            return params
        }
    },

    watch: {
        filters: {
            deep: true,
            handler: debounce(function () {
                this.onLoadData()
            }, 400)
        },

        dateFilters: {
            deep: true,
            handler () {
                this.onLoadData()
            }
        }
    },

    created () {
        const query = parseQueryParameters()

        if (query.filter) {
            for (const index in this.filter_options) {
                if (this.filter_options[index].key === query.filter) {
                    this.active_filter_index = index
                }
            }
        }

        if (query.facility_number) {
            this.filters.facility_number = query.facility_number
        }

        this.$eventManager.on("cycle-tenant-import", (response) => {
            this.uploadResponse = response
        })
        this.$eventManager.on("update-cycles", this.onLoadData.bind(this))
    },

    methods: {
        onChangeFilter (index) {
            this.active_filter_index = index

            this.onLoadData()
        },

        /**
         * Change the pagination page when requested.
         *
         * @param object page Page
         *
         * @return null
         */
        onChangePage (page) {
            this.$refs.cycles_table.changePage(page)
        },

        /**
         * Make the API call to confirm move-outs complete.
         *
         * @param {object} cycle Cycle
         */
        async onCheckConfirmedStatus (cycle) {
            try {
                this.loading_confirm_cycle_id = cycle.id

                const {
                    data: {
                        tenants_flagged: tenantsFlagged
                    }
                } = await http(`/api/cycles/${cycle.id}/check-flagged`)

                const confirmation = confirm(`There are ${tenantsFlagged.toLocaleString()} tenants flagged for this cycle. Continue?`)

                if (confirmation) {
                    await http.post(`/api/cycles/${cycle.id}/confirm-move-outs`)

                    this.$refs.cycles_table.reload()
                }
            } catch (ex) {
                this.$modalManager.add({ state: "bad", header: "Error", body: ex })
            } finally {
                this.loading_confirm_facility_id = null
            }
        },

        onEditCycle (cycle) {
            const componentConstructor = Vue.extend(EditCycleModal)

            this.$eventManager.emit("show-modal", {
                component: componentConstructor,
                props: {
                    cycle: cycle,
                    onSuccess: () => {
                        this.onLoadData()
                    }
                }
            })
        },

        /**
         * Make the table API call. This is used by the vue-table component when fetching data.
         *
         * @param {string} API call URL
         * @param {object} options
         *
         * @param {Promise}
         */
        onHttpFetch (url, options) {
            return http.get(url, options)
        },

        /**
         * Tell the table to fetch new data. Filters will be auto-applied.
         */
        onLoadData () {
            Vue.nextTick(() => {
                setTimeout(() => {
                    this.$refs.cycles_table.reload()
                }, 0)
            })
        },

        onNewCycle () {
            const componentConstructor = Vue.extend(NewCycleModal)

            this.$eventManager.emit("show-modal", {
                component: componentConstructor,
                props: {
                    onSuccess: (newCycleId) => {
                        this.newCycleId = newCycleId

                        this.onLoadData()
                    }
                }
            })
        },

        /**
         * Update the pagination component when data is received.
         *
         * @param {object} paginationData Pagination data
         *
         * @return {null}
         */
        onPaginationData (paginationData) {
            this.$refs.cycles_table_pagination.setPaginationData(paginationData)
        },

        onResetFilters () {
            for (const key in this.filters) {
                this.filters[key] = ""
            }

            for (const key in this.dateFilters) {
                this.dateFilters[key].start = ""
                this.dateFilters[key].end = ""
            }
        },

        /**
         * Set the row className for the newly created cycle.
         *
         * @param {object} dataItem Data item
         * @param {number} Row index
         *
         * @return {string}
         */
        onRowClass (dataItem, index) {
            const classNames = []

            if (dataItem.id === this.newCycleId) {
                classNames.push("newly-created")
            }

            return classNames.join(" ")
        },

        /**
         * Get the loaded record count when the table successfully loads data.
         *
         * @param {object} data Server response data object
         */
        onTableLoadSuccess ({ data: response }) {
            // this.last_row_count = response.data.length

            for (const index in this.filter_options) {
                const key = this.filter_options[index].key

                if (response.stats.move_outs[key] !== undefined) {
                    this.filter_options[index].badge = response.stats.move_outs[key]
                } else if (response.stats.tenant_thresholds[key] !== undefined) {
                    this.filter_options[index].badge = response.stats.tenant_thresholds[key]
                } else if (response.stats.letters[key] !== undefined) {
                    this.filter_options[index].badge = response.stats.letters[key]
                }
            }
        },

        /**
         * Toggle the detail row for the clicked record.
         *
         * @param object data   Row data
         * @param Event  ev    Event object
         *
         * @return null
         */
        onToggleDetailRow (data, ev) {
            for (const index in ev.composedPath()) {
                // let columns prevent clicking from happening
                if (ev.composedPath()[index].classList.contains("no-click")) {
                    return
                }

                // don't check past the table element
                if (ev.composedPath()[index].classList.contains("vuetable")) {
                    break
                }
            }

            this.$refs.cycles_table.toggleDetailRow(data.id)
        },

        onUploadTenants (cycleId) {
            const componentConstructor = Vue.extend(ImportTenantsModal)

            this.$eventManager.emit("show-modal", {
                component: componentConstructor,
                events: {
                    "success": (uploadResponse) => {
                        this.onLoadData()

                        this.uploadResponse = uploadResponse
                    }
                },
                props: {
                    cycleId
                }
            })
        }
    }
}
</script>

<style scoped>
.table-wrapper >>> .newly-created {
    background: #d1ffea;
}

.page /deep/ .vdp-datepicker__calendar {
    top: auto;
}
</style>



// WEBPACK FOOTER //
// src/pages/ManageCyclesPage.vue